import { useState, useEffect } from "react";
import { api, fmtMoney } from "../../lib/api";
import { StatCard } from "../../components/shared/StatCard";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, Target, DollarSign, Wallet, Star } from "lucide-react";

const COLORS = ["var(--cc-accent)", "#3B82F6", "#3B82F6", "#0EA5E9", "#64748B", "#38BDF8", "#94A3B8"];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState(null);
  useEffect(() => {
    api.get("/ops/analytics").then((r) => setData(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Compiling reports...</p>;

  const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

  return (
    <div className="space-y-6" data-testid="ops-analytics-page">
      <div>
        <p className="overline-cc">Intelligence</p>
        <h1 className="page-title mt-1.5">Analytics & Reports</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="analytics-kpi-paid" label="Total Collected" value={fmtMoney(data.total_paid)} icon={DollarSign} accent />
        <StatCard testId="analytics-kpi-outstanding" label="Outstanding" value={fmtMoney(data.total_outstanding)} icon={Wallet} />
        <StatCard testId="analytics-kpi-conversion" label="Lead Conversion" value={`${data.conversion_rate}%`} icon={Target} />
        <StatCard testId="analytics-kpi-avg" label="Avg Job Value" value={fmtMoney(data.avg_job_value)} icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-6">Revenue vs Missions</h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.monthly}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" type="monotone" dataKey="revenue" stroke="var(--cc-accent)" strokeWidth={2} dot={false} name="Revenue" />
              <Line yAxisId="r" type="monotone" dataKey="missions" stroke="#3B82F6" strokeWidth={2} dot={false} name="Missions" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-6">Revenue by Service</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.service_revenue} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={3}>
                {data.service_revenue.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-6">Lead Generation Trend</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.monthly}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="leads" fill="#3B82F6" radius={[6, 6, 0, 0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border bg-card p-6">
          <h2 className="font-heading font-semibold mb-6">Pipeline Distribution</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.leads_by_stage.filter((s) => s.count > 0)} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="stage" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="var(--cc-accent)" radius={[0, 6, 6, 0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {reviews && (
        <div className="rounded-xl border bg-card p-6" data-testid="analytics-reviews-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-heading font-semibold">Customer Reviews</h2>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-2xl" data-testid="analytics-avg-rating">{reviews.avg_rating ?? "—"}</span>
              <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              <span className="text-xs text-muted-foreground">{reviews.submitted_count} submitted · {reviews.requested_count} awaiting</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {reviews.reviews.filter((r) => r.status === "submitted").slice(0, 6).map((r) => (
              <div key={r.id} className="rounded-lg border p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40"}`} />)}</div>
                  <span className="font-mono-cc text-[10px] text-muted-foreground">{r.mission_number}</span>
                </div>
                {r.comment && <p className="text-xs text-muted-foreground mt-2 italic">"{r.comment}"</p>}
                <p className="text-xs font-semibold mt-2">{r.customer_name}</p>
              </div>
            ))}
            {reviews.reviews.filter((r) => r.status === "submitted").length === 0 && (
              <p className="text-xs text-muted-foreground col-span-full">No reviews submitted yet. Requests go out automatically the moment a job completes.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
